import { Card, SMBSelect, Page, PageTitleWithBack } from '../../../components';
import {
  Grid,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Typography,
  MenuItem,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { HomeWork, Save, Close } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  getAgentNeighborhoods,
  getLotsForNeighborhood,
  getNeighborhoodSeriesForJob,
  putChangeNeighborhoodSeries
} from '../../../fetch';
import { getNeighborhoodSeries, getGarageOptions } from '../../../fetch/lookups/sales-portal';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { IDropdownResponse } from '../../../models';

export const ChangeSeries = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [isSaving, setSaving] = useState<boolean>(false);
  const [neighborhoodId, setNeighborhoodId] = useState<string | number>('');
  const [lotId, setLotId] = useState<string | number>('');
  const [neighborhoodSeriesId, setNeighborhoodSeriesId] = useState<string | number>('');
  const [selectedGarageOptionValue, setSelectedGarageOptionValue] = useState<string>('');

  const { isLoading: isLoadingNeighborhoods, data: neighborhoods } = useQuery<
    IDropdownResponse[],
    Error
  >(['getAgentNeighborhoods'], () => getAgentNeighborhoods());

  const { isLoading: isLoadingLots, data: lots } = useQuery<IDropdownResponse[], Error>(
    ['getLotsForNeighborhood', neighborhoodId],
    () => getLotsForNeighborhood(neighborhoodId),
    {
      enabled: !!neighborhoodId
    }
  );

  const { isLoading: isLoadingNeighborhoodSeries, data: neighborhoodSeries } = useQuery<
    IDropdownResponse[],
    Error
  >(['getNeighborhoodSeries', neighborhoodId], () => getNeighborhoodSeries(neighborhoodId), {
    enabled: !!neighborhoodId
  });

  const {
    isLoading: isLoadingNeighborhoodSeriesForJob,
    data: neighborhoodSeriesForJob,
    refetch: refetchNeighborhoodSeriesForJob
  } = useQuery<any, Error>(
    ['getNeighborhoodSeriesForJob', lotId],
    () => getNeighborhoodSeriesForJob(lotId),
    {
      onSuccess: d => {
        const matchedSeries = neighborhoodSeries?.find(
          series => series.description === d.neighborhoodSeriesName
        );
        if (matchedSeries?.value) {
          setNeighborhoodSeriesId(matchedSeries?.value);
        }
      },
      enabled: !!lotId && !!neighborhoodSeries
    }
  );

  const { isLoading: isLoadingGarageOptions, data: garageOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getGarageOptions', lotId], () => getGarageOptions(), {
    onSuccess: d => {
      const options = d.filter(record => {
        return neighborhoodSeriesForJob?.selectedGarageOptions?.includes(record.value);
      });
      setSelectedGarageOptionValue(options?.[0]?.value);
    },
    enabled: !!neighborhoodSeriesForJob
  });
  const garageOptionsData = useMemo(() => garageOptions ?? [], [garageOptions]);

  const isDirty = () => {
    const matchedSeries = neighborhoodSeries?.find(
      series => series?.description === neighborhoodSeriesForJob?.neighborhoodSeriesName
    );

    const isDifferent =
      !neighborhoodSeriesForJob?.selectedGarageOptions?.includes(selectedGarageOptionValue) ||
      neighborhoodSeriesId !== matchedSeries?.value;

    return isDifferent;
  };

  return (
    <Page title={'Change Neighborhood Series'}>
      <PageTitleWithBack
        showDivider={false}
        backText="Back to Sales Dashboard"
        title="Change Neighborhood Series"
        backUrl="/sales/dashboard"
      />
      <Box mt={1}>
        <Card title="Change Neighborhood Series" icon={<HomeWork />}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container alignItems="flex-start" spacing={1}>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    name="neighborhoodId"
                    label="Neighborhood"
                    id="neighborhoodId"
                    variant="outlined"
                    disabled={isLoadingNeighborhoods}
                    value={neighborhoodId}
                    required
                    onChange={({ target: { value } }) => {
                      setNeighborhoodId(value);
                      setLotId('');
                      setNeighborhoodSeriesId('');
                      setSelectedGarageOptionValue('');
                    }}
                  >
                    {neighborhoods &&
                      neighborhoods.map((neighborhood, index) => {
                        return (
                          <MenuItem key={`${index}`} value={neighborhood.value}>
                            {neighborhood.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    name="lotId"
                    label="Lot"
                    id="lotId"
                    variant="outlined"
                    required
                    disabled={!neighborhoodId || isLoadingLots}
                    value={lotId}
                    onChange={({ target: { value } }) => {
                      setLotId(Number(value));
                    }}
                  >
                    {lots &&
                      lots.map((lot, index) => {
                        return (
                          <MenuItem key={`${index}`} value={lot.value}>
                            {lot.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <Typography className={classes.readOnlyHeaders} variant="h6">
                      Current Neighborhood Series
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      {neighborhoodSeriesForJob ? (
                        <>
                          {neighborhoodSeriesForJob?.neighborhoodSeriesName} -{' '}
                          {neighborhoodSeriesForJob?.planStyles?.join(', ')}
                        </>
                      ) : (
                        <span className={classes.notSelected}>Not Selected</span>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    required={true}
                    name="neighborhoodSeriesId"
                    label="Plan Series"
                    id="neighborhoodSeriesId"
                    variant="outlined"
                    disabled={!neighborhoodId || isLoadingNeighborhoodSeries}
                    value={neighborhoodSeriesId}
                    onChange={({ target: { value } }) => {
                      setNeighborhoodSeriesId(Number(value));
                    }}
                  >
                    {neighborhoodSeries &&
                      neighborhoodSeries.map((series, index) => {
                        return (
                          <MenuItem key={`${index}`} value={series.value}>
                            {series.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    sx={{ display: garageOptionsData.length > 0 ? 'block' : 'none' }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    required
                  >
                    <Typography>
                      {isLoadingGarageOptions ? 'Loading...' : 'Available Garage Options'}
                    </Typography>
                    <RadioGroup
                      value={selectedGarageOptionValue}
                      onChange={e => setSelectedGarageOptionValue(e.target.value)}
                      aria-label="hasInspectionDept"
                      name="availableGarageOptions"
                    >
                      {garageOptionsData?.map((option, index) => {
                        return (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio />}
                            label={option.description}
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Box>
      <Box mt={1} mb={0.5}>
        <Divider />
      </Box>
      <ButtonGroup>
        <LoadingButton
          loading={isSaving}
          loadingPosition="start"
          variant="contained"
          color="primary"
          disabled={
            isLoadingNeighborhoods ||
            isLoadingNeighborhoodSeriesForJob ||
            isLoadingGarageOptions ||
            isSaving ||
            !neighborhoodSeriesId ||
            !isDirty() ||
            !lotId
          }
          startIcon={<Save />}
          onClick={async () => {
            try {
              setSaving(true);
              const garageOptionValues = [selectedGarageOptionValue];
              await putChangeNeighborhoodSeries(lotId, {
                neighborhoodId: neighborhoodId,
                seriesId: neighborhoodSeriesId,
                garageOptions: garageOptionValues
              });
              refetchNeighborhoodSeriesForJob();
              enqueueSnackbar(`Neighborhood Series Updated!`, {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(
                error?.Detail ?? `Error updating neighborhood series, please try again.`,
                {
                  variant: 'error'
                }
              );
            } finally {
              setSaving(false);
            }
          }}
        >
          Save
        </LoadingButton>
        <Button
          type="button"
          variant="contained"
          color="inherit"
          startIcon={<Close />}
          onClick={() => {
            if (isDirty()) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to cancel?'
              );
              if (result) {
                setNeighborhoodId('');
                setLotId('');
                setNeighborhoodSeriesId('');
                setSelectedGarageOptionValue('');
              } else {
                return;
              }
            }
            setNeighborhoodId('');
            setLotId('');
            setNeighborhoodSeriesId('');
            setSelectedGarageOptionValue('');
          }}
        >
          Cancel
        </Button>
      </ButtonGroup>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginTop: theme.spacing(1)
  },
  contractInfo: {
    fontWeight: '500',
    paddingRight: 8
  },
  readOnlyHeaders: {
    paddingBottom: theme.spacing(0.25),
    fontWeight: '700'
  },
  notSelected: {
    color: '#88888',
    opacity: 0.6
  },
  garageOptions: {
    '@media (min-width: 600px)': {
      paddingTop: '17px'
    }
  }
}));
